import type { PlatformPolicyConfig } from '../../lib/types';

export type ArchetypeKey = 'BRAHMIN' | 'KSHATRIYA' | 'VAISHYA' | 'SHUDRA';
export type SubScoreKey = 'hridaya' | 'vak' | 'jnana' | 'karma' | 'phala';
export type AdhikaraStageKey = 'BIJA' | 'ANKURA' | 'PARNA' | 'PUSHPA' | 'PHALA';

export type ArchetypePolarity = 'NAVANEETA' | 'BLADE' | 'SAMATA' | 'VISHVASA' | 'BLADE_INWARD' | 'KARMA_VAK';

export interface WeightSet {
  subScoreWeights: Record<SubScoreKey, number>;
  hridayaPolarity: ArchetypePolarity;
  vakPolarity: ArchetypePolarity;
  phalaDefinition: string;
  chatushpatScope: number;
  secondaryTeachingAffinityFor: ArchetypeKey;
  floorCondition?: Record<string, boolean>;
  systemicMultiplier?: boolean;
}

export interface StageWindow {
  range: readonly [number, number];
  label: string;
  meaning: string;
}

export const ADHIKARA_STAGE_WINDOWS: Record<AdhikaraStageKey, StageWindow> = {
  BIJA: { range: [0, 2], label: 'Bija / बीज', meaning: 'seed — exposure and Samskar' },
  ANKURA: { range: [3, 4], label: 'Ankura / अंकुर', meaning: 'sprout — pattern and practice' },
  PARNA: { range: [5, 6], label: 'Parna / पर्ण', meaning: 'leaf — application and fluency' },
  PUSHPA: { range: [7, 8], label: 'Pushpa / पुष्प', meaning: 'flower — mastery and expression' },
  PHALA: { range: [9, 9], label: 'Phala / फल', meaning: 'fruit — full Adhikara' },
} as const;

export const ARCHETYPE_WEIGHT_TABLE: Record<ArchetypeKey, WeightSet> = {
  BRAHMIN: {
    hridayaPolarity: 'NAVANEETA',
    vakPolarity: 'BLADE',
    subScoreWeights: {
      hridaya: 0.25,
      vak: 0.25,
      jnana: 0.30,
      karma: 0.10,
      phala: 0.10,
    },
    phalaDefinition: 'ABHAYAPRADA',
    chatushpatScope: 4,
    secondaryTeachingAffinityFor: 'VAISHYA',
  },
  KSHATRIYA: {
    hridayaPolarity: 'BLADE',
    vakPolarity: 'NAVANEETA',
    subScoreWeights: {
      hridaya: 0.20,
      vak: 0.20,
      jnana: 0.15,
      karma: 0.30,
      phala: 0.15,
    },
    phalaDefinition: 'PRAJA_PARIPALANA',
    chatushpatScope: 3,
    secondaryTeachingAffinityFor: 'SHUDRA',
  },
  VAISHYA: {
    hridayaPolarity: 'SAMATA',
    vakPolarity: 'VISHVASA',
    subScoreWeights: {
      hridaya: 0.15,
      vak: 0.20,
      jnana: 0.15,
      karma: 0.20,
      phala: 0.30,
    },
    phalaDefinition: 'POSHA',
    chatushpatScope: 1,
    secondaryTeachingAffinityFor: 'BRAHMIN',
    systemicMultiplier: true,
    floorCondition: { paripalana: true },
  },
  SHUDRA: {
    hridayaPolarity: 'BLADE_INWARD',
    vakPolarity: 'KARMA_VAK',
    subScoreWeights: {
      hridaya: 0.20,
      vak: 0.10,
      jnana: 0.20,
      karma: 0.25,
      phala: 0.25,
    },
    phalaDefinition: 'SEVA_PHALA',
    chatushpatScope: 1,
    secondaryTeachingAffinityFor: 'KSHATRIYA',
  },
} as const;

export const ACHARYA_CRITERIA_KEYS = [
  'vriddha',
  'alolupa',
  'atmavant',
  'dambhahin',
  'samyagvinita',
  'mridu',
  'minAdhikaraScore',
] as const;

export const RAJA_CRITERIA_KEYS = [
  'dana',
  'vrata',
  'brahmacharya',
  'brahma_dharanam',
  'dama',
  'prashantata',
  'bhuta_anukampana',
  'anrishansya',
  'parasva_adana_varjanam',
  'vyalika_karanam_tyaga',
  'matapitru_shushrusha',
  'devata_atithi_guru_pujanam',
  'ghrina',
  'nitya_indriya_sanyama',
] as const;

function readArchetype(policy: PlatformPolicyConfig | null | undefined, archetype: ArchetypeKey): WeightSet {
  const item = policy?.archetypes?.[archetype];
  if (!item || typeof item !== 'object') return ARCHETYPE_WEIGHT_TABLE[archetype];

  const candidate = item as Partial<WeightSet>;
  const weights = candidate.subScoreWeights;
  const validWeights = weights && ['hridaya', 'vak', 'jnana', 'karma', 'phala'].every((key) => typeof weights[key as SubScoreKey] === 'number')
    ? (weights as Record<SubScoreKey, number>)
    : ARCHETYPE_WEIGHT_TABLE[archetype].subScoreWeights;

  return {
    ...ARCHETYPE_WEIGHT_TABLE[archetype],
    ...candidate,
    subScoreWeights: validWeights,
  };
}

export function getArchetypeWeights(policy: PlatformPolicyConfig | null | undefined, archetype: ArchetypeKey): Record<SubScoreKey, number> {
  return readArchetype(policy, archetype).subScoreWeights;
}

export function getArchetypePolarity(policy: PlatformPolicyConfig | null | undefined, archetype: ArchetypeKey): Pick<WeightSet, 'hridayaPolarity' | 'vakPolarity'> {
  const resolved = readArchetype(policy, archetype);
  return {
    hridayaPolarity: resolved.hridayaPolarity,
    vakPolarity: resolved.vakPolarity,
  };
}

export function getStageWindows(policy?: PlatformPolicyConfig | null): Record<AdhikaraStageKey, StageWindow> {
  const stages = policy?.adhikara?.stages as Partial<Record<AdhikaraStageKey, StageWindow>> | undefined;
  if (!stages) return ADHIKARA_STAGE_WINDOWS;
  return {
    BIJA: stages.BIJA ?? ADHIKARA_STAGE_WINDOWS.BIJA,
    ANKURA: stages.ANKURA ?? ADHIKARA_STAGE_WINDOWS.ANKURA,
    PARNA: stages.PARNA ?? ADHIKARA_STAGE_WINDOWS.PARNA,
    PUSHPA: stages.PUSHPA ?? ADHIKARA_STAGE_WINDOWS.PUSHPA,
    PHALA: stages.PHALA ?? ADHIKARA_STAGE_WINDOWS.PHALA,
  };
}
